import { BACKEND_URL } from "@/lib/api";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Star, Instagram, ExternalLink, Quote, Ticket, MapPin, ChevronDown } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { CourseDialog } from "./CourseDialog";
import { SiteImage } from "./SiteImage";
import { MobileCollapse } from "./MobileCollapse";
import { trainerSlug } from "../../data/team";
import { TOPICS, TOPIC_LINKS } from "../../data/topics";
import { findCourse } from "../../data/courses";
import { CONTACT, COMMUNITY_EVENTS, STARTPASS, MYSPORTS_OFFERS_URL, FIRST_VISIT, selectTopic } from "../../data/content";
import { useContent } from "../../content/ContentContext";
import { useGoogleReviews } from "@/lib/googleReviews";

const nowHHMM = () => new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });

export const TodayCourses = () => {
  const [state, setState] = useState({ items: null });
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/kursplan`).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!d) return;
      const now = nowHHMM();
      const upcoming = (day) => (day?.items || []).filter((i) => !i.canceled && i.kind === "course" && (day.date > d.today || i.end_time > now));
      const day = d.days.filter((x) => x.date >= d.today).find((x) => upcoming(x).length > 0);
      const label = !day || day.date === d.today ? "heute" : new Date(`${day.date}T12:00:00`).toLocaleDateString("de-DE", { weekday: "long" });
      setState({ items: day ? upcoming(day) : [], label });
    }).catch(() => {});
  }, []);
  if (!state.items) return null;
  const show = state.items.slice(0, 5);
  const heading = !show.length ? "Diese Woche keine weiteren Kurse – schau in den Kursplan." : state.label === "heute" ? "Diese Kurse laufen heute noch:" : `Heute ist Schluss – am ${state.label} geht es weiter:`;
  return (
    <section data-testid="section-heute" className="py-6 md:py-8">
      <div className="max-w-[1320px] mx-auto px-5 md:px-10">
        <Reveal>
          <div data-testid="today-courses" className="rounded-3xl bg-[#161616] text-white p-6 md:p-7 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8 shadow-[0_24px_60px_-30px_rgba(22,22,22,0.6)]">
            <div className="shrink-0 lg:w-60">
              <p className="inline-flex items-center gap-2.5 font-head text-sm font-semibold text-white/70"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#9BE39B] opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#9BE39B]" /></span>Heute im Studio</p>
              <h2 className="font-head text-base md:text-lg font-bold mt-1">{heading}</h2>
            </div>
            <ul className="flex flex-wrap gap-2 flex-1">
              {show.map((i) => (
                <li key={i.id}>
                  <button data-testid="today-course-btn" onClick={() => setSelected(i)} className="inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/5 hover:bg-white/15 transition-colors px-4 py-2 text-sm">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: i.color }} aria-hidden="true" />
                    <span className="font-semibold tabular-nums">{i.start_time}</span><span>{findCourse(i.name)?.title || i.name}</span>
                    {i.spots_left !== null && i.spots_left !== undefined && <span className={`text-xs ${i.spots_left <= 0 ? "text-white/50" : "text-[#9BE39B]"}`}>{i.spots_left <= 0 ? "voll" : `${i.spots_left} frei`}</span>}
                  </button>
                </li>
              ))}
              {!show.length && <li className="text-sm text-white/70">Schau in den Kursplan für die nächsten Tage.</li>}
            </ul>
            <Link to="/kursplan" data-testid="today-plan-link" className="tap btn-light !text-sm self-start lg:self-auto whitespace-nowrap"><CalendarDays size={16} />Ganzer Kursplan <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
        <CourseDialog course={selected} onClose={() => setSelected(null)} />
      </div>
    </section>
  );
};

export const TeamStrip = () => {
  const { team } = useContent();
  return (
  <section id="team" data-testid="section-team-strip" className="py-10 md:py-20 scroll-mt-24 bg-white border-y border-[#E6E3DD]">
    <div className="max-w-[1320px] mx-auto px-5 md:px-10 grid lg:grid-cols-[1fr_1.6fr] gap-6 lg:gap-16 items-center">
      <div>
        <p className="eyebrow mb-3" data-testid="team-eyebrow">Dein Trainerteam</p>
        <h2 data-testid="team-heading" className="h-display text-2xl sm:text-3xl lg:text-4xl">Sechs Coaches. Ein Team, das dich kennt.</h2>
        <Link to="/ueber-uns#team" data-testid="team-strip-link" className="tap mt-6 hidden md:inline-flex items-center gap-2 text-sm font-semibold hover:text-[#8A0093]">Das Team kennenlernen <ArrowRight size={16} /></Link>
      </div>
      <MobileCollapse id="team-strip" label="Unsere sechs Coaches ansehen">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 md:gap-4">
        {team.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.05}>
            <Link to={`/ueber-uns#trainer-${trainerSlug(t.name)}`} data-testid={`team-strip-${trainerSlug(t.name)}`} className="group block text-center">
              <div className="aspect-square rounded-full overflow-hidden bg-[#ECEBE6] ring-2 ring-transparent group-hover:ring-[#BA00C5] transition-[box-shadow,transform] duration-300 group-hover:-translate-y-1">
                <SiteImage src={t.img} alt={`${t.name}, ${t.role}`} sizes="120px" width={200} height={200} className="w-full h-full object-cover" />
              </div>
              <p className="mt-2 font-head text-sm font-semibold leading-tight">{t.name.split(" ")[0]}</p>
              <p className="text-[11px] muted leading-tight mt-0.5">{t.role}</p>
            </Link>
          </Reveal>
        ))}
      </div>
      <Link to="/ueber-uns#team" data-testid="team-strip-link-mobile" className="tap mt-5 inline-flex md:hidden items-center gap-2 text-sm font-semibold hover:text-[#8A0093]">Das Team kennenlernen <ArrowRight size={16} /></Link>
      </MobileCollapse>
    </div>
  </section>
  );
};

export const FirstVisit = ({ compact = false }) => {
  const [open, setOpen] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches);
  return (
    <div data-testid="first-visit" className={compact ? "mt-8 pt-6 border-t border-[#E6E3DD]" : "mt-8 md:mt-10 card p-6 md:p-8"}>
      <button type="button" data-testid="first-visit-toggle" aria-expanded={open} aria-controls="first-visit-body" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 text-left min-h-[44px]">
        <span className="w-10 h-10 shrink-0 rounded-full bg-[#BA00C5]/10 text-[#8A0093] flex items-center justify-center"><MapPin size={18} /></span>
        <span className={`font-head font-bold flex-1 ${compact ? "text-base" : "text-lg md:text-xl"}`}>{FIRST_VISIT.title}</span>
        <ChevronDown size={18} className={`shrink-0 text-[#8A0093] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <dl id="first-visit-body" className={`mt-5 grid gap-x-8 gap-y-4 text-sm ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-5"}`}>
        {FIRST_VISIT.items.map(([k, v]) => <div key={k} data-testid={`first-visit-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}><dt className="font-head font-semibold">{k}</dt><dd className="mt-1 muted leading-relaxed">{v}</dd></div>)}
      </dl>}
      {!open && <p className="mt-2 pl-[52px] text-xs muted">Parken, Kleidung, Ankommen, Ablauf, Kosten – antippen zum Aufklappen.</p>}
    </div>
  );
};

export const StartSteps = () => (
  <section id="so-gehts" data-testid="section-start-steps" className="py-10 md:py-14 scroll-mt-24 bg-white border-y border-[#E6E3DD]">
    <div className="max-w-[1320px] mx-auto px-5 md:px-10">
      <Reveal>
        <div data-testid="startpass-card" className="rounded-3xl bg-[#161616] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <span className="w-12 h-12 shrink-0 rounded-2xl bg-[#BA00C5] flex items-center justify-center"><Ticket size={22} /></span>
          <div className="flex-1 min-w-0">
            <p className="font-head text-sm font-semibold text-white/60">Nach dem Probetraining</p>
            <h3 className="font-head text-lg md:text-xl font-bold mt-1">{STARTPASS.name} für {STARTPASS.price}</h3>
            <p className="mt-1.5 text-sm text-white/75 leading-relaxed">Zwei Wochen bis zu 2 Kurse pro Tag plus Open Gym testen – nur für Neukunden, ohne Mitgliedschaft, ohne Kündigung.</p>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col gap-2.5 shrink-0">
            <a href={MYSPORTS_OFFERS_URL} target="_blank" rel="noopener noreferrer" data-testid="startpass-book-link" className="btn-light !text-sm justify-center whitespace-nowrap">Startpass buchen <ExternalLink size={14} /></a>
            <Link to="/preise" data-testid="startpass-prices-link" className="tap inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white">Alle Preise <ArrowRight size={14} /></Link>
          </div>
        </div>
      </Reveal>
      <p className="mt-6 text-sm muted">Praktisches zu Anfahrt, Parken und Mitbringen: <a href="#kontakt" data-testid="first-visit-link" className="tap font-semibold underline underline-offset-4 hover:text-[#8A0093]">Anfahrt & erster Besuch</a></p>
    </div>
  </section>
);

const CoachFaces = ({ team }) => (
  <Link to="/ueber-uns#team" data-testid="people-team-link" className="group mt-7 inline-flex items-center gap-4">
    <span className="flex -space-x-2.5 shrink-0">
      {team.slice(0, 6).map((t) => <SiteImage key={t.name} src={t.img} alt={`${t.name}, ${t.role}`} sizes="48px" width={96} height={96} data-testid={`people-coach-${trainerSlug(t.name)}`} className="w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 shrink-0 rounded-full object-cover ring-2 ring-[#161616] transition-transform duration-300 group-hover:-translate-y-0.5" />)}
    </span>
    <span className="text-sm leading-tight"><span className="block font-head font-semibold">{team.length} Coaches, die dich kennen</span><span className="text-white/70 group-hover:text-white inline-flex items-center gap-1">Team kennenlernen <ArrowRight size={13} /></span></span>
  </Link>
);

export const CommunityBand = () => {
  const { images, team } = useContent();
  return (
    <section id="team" data-testid="section-community-band" className="relative overflow-hidden text-white bg-[#161616] py-12 md:py-16 scroll-mt-24">
      <div className="max-w-[1320px] mx-auto px-5 md:px-10 grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-14 items-center">
        <Reveal>
          <p className="inline-flex items-center gap-2.5 font-head text-sm font-semibold text-white/80 mb-4"><span className="w-2 h-2 rounded-full bg-[#E9B3EE]" />Community & Team</p>
          <h2 data-testid="community-band-heading" className="h-display text-3xl sm:text-4xl lg:text-5xl max-w-2xl">Hier kennt man dich beim Namen.</h2>
          <p className="mt-5 text-base md:text-lg text-white/80 max-w-xl leading-relaxed">So viel Gemeinschaft, wie du möchtest – wer nach dem Kurs direkt nach Hause fährt, gehört genauso dazu.</p>
          <ul data-testid="community-events" className="mt-4 flex flex-wrap gap-2">
            {COMMUNITY_EVENTS.slice(0, 4).map((e) => <li key={e} className="rounded-full bg-white/12 border border-white/20 px-3.5 py-1.5 text-xs md:text-sm font-head font-semibold">{e}</li>)}
          </ul>
          <CoachFaces team={team} />
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
            <button data-testid="community-band-cta" onClick={() => selectTopic("einsteiger")} className="tap inline-flex items-center gap-1.5 text-sm font-semibold text-white underline underline-offset-4 decoration-[#E9B3EE]/60 hover:text-[#E9B3EE]">Kostenloses Probetraining <ArrowRight size={16} /></button>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="relative">
          <div className="absolute -inset-6 rounded-[2rem] bg-[#BA00C5]/20 blur-3xl pointer-events-none" aria-hidden="true" />
          <div data-testid="community-collage" className="relative aspect-[3/2] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] card-hover">
            <SiteImage src={images.community} alt="Foto-Collage der FlensFitness-Community: Kurse, Wettkämpfe, Events und Training draußen" sizes="(min-width: 1024px) 720px, 100vw" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="max-w-[1320px] mx-auto px-5 md:px-10 mt-8 md:mt-12">
        <p data-testid="people-corporate" className="text-sm text-white/85"><span className="font-head font-semibold text-white">Epassi oder Wellpass?<sup>*</sup></span> Bei uns trainierst du ohne Zusatzkosten. <a href="/firmenfitness/freischalten" data-testid="people-corporate-unlock" className="tap font-semibold text-white underline underline-offset-4 decoration-[#E9B3EE]/60 hover:text-[#E9B3EE]">Jetzt freischalten</a> · <Link to="/firmenfitness" data-testid="people-corporate-more" className="tap underline underline-offset-4 hover:text-white">Mehr zu Firmenfitness</Link><span data-testid="people-corporate-wellpass-note" className="block mt-1 text-xs text-white/55">* Wellpass nur noch bis 31.08.2027 – sofern dein Arbeitgeber Wellpass vor dem 01.09.2026 abgeschlossen hat.</span></p>
      </div>
    </section>
  );
};

const Stars = ({ n }) => <span role="img" className="flex gap-0.5" aria-label={`${n} von 5 Sternen`}>{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={15} className={i <= n ? "fill-[#F2B705] text-[#F2B705]" : "text-[#D9D5CE]"} />)}</span>;

const GoogleMark = () => <span role="img" aria-label="Google" className="inline-flex items-center gap-1 font-head font-semibold text-xs"><span aria-hidden="true" className="text-[#4285F4]">G</span><span aria-hidden="true" className="text-[#EA4335]">o</span><span aria-hidden="true" className="text-[#FBBC05]">o</span><span aria-hidden="true" className="text-[#4285F4]">g</span><span aria-hidden="true" className="text-[#34A853]">l</span><span aria-hidden="true" className="text-[#EA4335]">e</span></span>;

const REVIEW_LIMIT = 170;
const ReviewText = ({ text, idx }) => {
  const [open, setOpen] = useState(false);
  const long = text.length > REVIEW_LIMIT + 30;
  const shown = open || !long ? text : `${text.slice(0, text.lastIndexOf(" ", REVIEW_LIMIT))} …`;
  return (
    <blockquote className="mt-4 text-sm md:text-[15px] text-[#3A3A3E] leading-relaxed flex-1">
      {shown}
      {long && <button type="button" data-testid={`review-toggle-${idx}`} onClick={() => setOpen((v) => !v)} className="tap ml-1.5 text-xs font-semibold text-[#8A0093] underline underline-offset-4">{open ? "weniger" : "mehr lesen"}</button>}
    </blockquote>
  );
};

export const Reviews = () => {
  const { reviews } = useContent();
  const google = useGoogleReviews();
  const rating = google?.rating ?? reviews.rating;
  const count = google?.count ?? reviews.count;
  const url = google?.maps_url || reviews.google_url;
  const googleItems = (google?.reviews || []).filter((r) => r.text && (r.stars ?? 5) >= 4).slice(0, 3).map((r) => ({ ...r, source: "Google" }));
  const items = [...reviews.items, ...googleItems];
  return (
    <section id="stimmen" data-testid="section-stimmen" className="pt-10 md:pt-14 pb-12 md:pb-16 scroll-mt-24 bg-[#F7F6F3]">
      <div className="max-w-[1320px] mx-auto px-5 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 md:gap-6">
          <SectionHead id="stimmen" eyebrow="Menschen statt Mitgliedsnummern" title="Das sagen Mitglieder über uns." />
          <Reveal className="-mt-4 md:mt-0">
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link" data-live={google ? "true" : undefined} className="card card-hover inline-flex items-center gap-4 px-5 py-3 md:py-4 mb-6 md:mb-10">
              <span className="h-display text-3xl">{String(rating).replace(".", ",")}</span>
              <span><Stars n={Math.round(rating)} /><span className="block text-xs muted mt-1">{count ? `${count} Bewertungen` : "Bewertungen"} bei <GoogleMark /> <ExternalLink size={11} className="inline ml-1" /></span></span>
            </a>
          </Reveal>
        </div>
        <div data-testid="reviews-scroller" className="swipe-row md:grid md:grid-cols-3 gap-4 md:gap-5">
          {items.map((t, i) => (
            <Reveal key={`${t.name}-${i}`} delay={Math.min(i, 5) * 0.08} className="swipe-item md:w-auto">
              <figure data-testid={`review-${i}`} data-source={t.source} className="card card-hover h-full p-6 md:p-7 flex flex-col">
                <div className="flex items-center justify-between"><Stars n={t.stars ?? 5} />{t.source === "Google" ? <GoogleMark /> : <Quote className="text-[#E9B3EE]" size={22} />}</div>
                <ReviewText text={t.text} idx={i} />
                <figcaption className="mt-6 flex items-center gap-3">
                  {t.img ? <SiteImage src={t.img} alt={t.name} width={40} height={40} className="w-10 h-10 rounded-full object-cover" />
                    : <span className="w-10 h-10 rounded-full bg-[#F7F6F3] border border-[#E6E3DD] flex items-center justify-center font-head font-bold" aria-hidden="true">{t.name[0]}</span>}
                  <span className="text-sm min-w-0">
                    <span className="block font-head font-semibold truncate">{t.source === "Google" && t.author_url ? <a href={t.author_url} target="_blank" rel="noopener noreferrer" className="hover:underline">{t.name}</a> : t.name}</span>
                    <span className="text-xs muted">{t.source === "Google" ? <>Google-Bewertung{t.when ? ` · ${t.when}` : ""}{t.url && <> · <a href={t.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">Auf Google ansehen</a></>}</> : "Mitglied bei FlensFitness"}</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <p className="md:hidden mt-3 text-xs muted text-center" aria-hidden="true">Wischen für mehr Stimmen →</p>
        {google && <p data-testid="google-attribution" className="mt-4 text-xs muted">Google-Bewertungen werden live über Google Maps geladen (Auswahl nach Google-Relevanz, nur Bewertungen mit Text). Alle Bewertungen: <a href={url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">Google Maps</a>.</p>}
      </div>
    </section>
  );
};

export const InstagramBlock = () => {
  const { instagram } = useContent();
  if (!instagram.photos?.length) return null;
  return (
    <section id="instagram" data-testid="section-instagram" className="py-14 md:py-20 scroll-mt-24 bg-white border-y border-[#E6E3DD]">
      <div className="max-w-[1320px] mx-auto px-5 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8 md:mb-10">
          <div>
            <p className="eyebrow mb-3">Community</p>
            <h2 data-testid="instagram-heading" className="h-display text-2xl sm:text-3xl lg:text-4xl">{instagram.tagline || "Momente aus dem Studio."}</h2>
          </div>
          <a href={instagram.url} target="_blank" rel="noopener noreferrer" data-testid="instagram-profile-link" className="btn-ghost !text-sm"><Instagram size={16} />@{instagram.handle}</a>
        </div>
        <div className={`grid grid-cols-2 ${instagram.photos.length > 4 ? "md:grid-cols-3 lg:grid-cols-6" : "md:grid-cols-4"} gap-3 md:gap-4`}>
          {instagram.photos.map((p, i) => (
            <Reveal key={`${p.url}-${i}`} delay={i * 0.06}>
              <a href={p.link || instagram.url} target="_blank" rel="noopener noreferrer" data-testid={`instagram-photo-${i}`} className="group relative block aspect-square rounded-2xl overflow-hidden bg-[#ECEBE6]">
                <img src={p.url.startsWith("/api/") ? `${BACKEND_URL}${p.url}` : p.url} alt={p.caption || `Instagram-Foto ${i + 1} von FlensFitness`} loading="lazy" className="w-full h-full object-cover object-[center_25%] transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                {p.caption && <span className="absolute bottom-3 left-3 right-3 text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">{p.caption}</span>}
                <Instagram size={16} className="absolute top-3 right-3 text-white drop-shadow" />
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const TopicTile = ({ t, className = "" }) => (
  <li className={className}>
    <Link to={`/${t.slug}`} data-testid={`home-topic-${t.slug}`} className="group card h-full min-h-[64px] px-5 py-4 flex items-center justify-between gap-3 transition-colors hover:border-[#BA00C5]/50">
      <span className="min-w-0">
        <span className="block font-head font-semibold leading-tight">{t.label}</span>
        <span className="block text-xs muted mt-1 truncate">{TOPICS[t.slug].eyebrow}</span>
      </span>
      <ArrowRight size={16} className="shrink-0 transition-transform group-hover:translate-x-1" />
    </Link>
  </li>
);

const MOBILE_VISIBLE = 3;

export const TopicTiles = () => {
  const [open, setOpen] = useState(false);
  const rest = TOPIC_LINKS.length - MOBILE_VISIBLE;
  return (
    <section id="themen" data-testid="section-topics" className="py-10 md:py-16 scroll-mt-24">
      <div className="max-w-[1320px] mx-auto px-5 md:px-10">
        <p className="eyebrow mb-3" data-testid="topics-eyebrow">Weitere Themen</p>
        <h2 data-testid="topics-heading" className="h-display text-2xl sm:text-3xl">Dein Ziel in Flensburg – <span className="accent">dein Einstieg.</span></h2>
        <ul id="home-topics-list" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TOPIC_LINKS.map((t, i) => (
            <TopicTile key={t.slug} t={t} className={i >= MOBILE_VISIBLE && !open ? "hidden md:block" : ""} />
          ))}
        </ul>
        {rest > 0 && (
          <button type="button" data-testid="home-topics-toggle" aria-expanded={open} aria-controls="home-topics-list" onClick={() => setOpen((v) => !v)}
            className="md:hidden mt-3 inline-flex items-center gap-2 min-h-[44px] text-sm font-head font-semibold text-[#8A0093]">
            {open ? "Weniger anzeigen" : `${rest} weitere Themen anzeigen`}<ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
    </section>
  );
};
